import { ordenarAchados, type Achado, type Severidade } from '@social/shared';
import { Etiqueta } from './ui';

const ROTULO: Record<Severidade, string> = {
  IMPEDITIVO: 'Impede',
  EXIGENCIA: 'Exigência',
  AVISO: 'Aviso',
};

const EXPLICACAO: Record<Severidade, string> = {
  IMPEDITIVO: 'O contrato não é gerado enquanto isto existir.',
  EXIGENCIA: 'Sai, mas a Junta provavelmente devolve.',
  AVISO: 'Não impede nada. É sinal de dado copiado.',
};

/** Tom do selo por severidade — reaproveita a `Etiqueta` do design system. */
const TOM: Record<Severidade, 'erro' | 'alerta' | 'neutro'> = {
  IMPEDITIVO: 'erro',
  EXIGENCIA: 'alerta',
  AVISO: 'neutro',
};

/** Faixa à esquerda do achado, na mesma cor do selo. */
const BORDA: Record<Severidade, string> = {
  IMPEDITIVO: 'border-erro-600',
  EXIGENCIA: 'border-alerta-600',
  AVISO: 'border-tinta-300',
};

interface Props {
  achados: readonly Achado[];
  carregando: boolean;
  /** Sem isto, laudo vazio antes da conferência viraria "está tudo certo". */
  conferido: boolean;
}

/**
 * O laudo do validador, ao lado do formulário.
 *
 * Não é um alerta no fim: é a coluna que acompanha o trabalho. O contador vê o
 * que impede a geração enquanto preenche, e não depois de clicar em salvar —
 * que é justamente a diferença entre este produto e o Word.
 *
 * `aria-live="polite"`: o painel muda com frequência, e `assertive` faria o
 * leitor de tela interromper a digitação a cada mudança.
 */
export function PainelLaudo({ achados, carregando, conferido }: Props): JSX.Element {
  const ordenados = ordenarAchados(achados);
  const impeditivos = ordenados.filter((a) => a.severidade === 'IMPEDITIVO').length;

  return (
    <aside
      aria-live="polite"
      aria-busy={carregando}
      className="border-tinta-100 bg-superficie flex flex-col gap-3 rounded-xl border p-4 lg:sticky lg:top-4"
    >
      <h2 className="text-tinta-900 text-lg font-semibold">Conferência</h2>

      {carregando && <p className="text-tinta-500 text-sm">Conferindo…</p>}

      {!carregando && !conferido && (
        <p className="text-tinta-500 text-sm">
          Clique em <strong className="text-tinta-700 font-medium">Conferir</strong> para
          o sistema checar o cadastro.
        </p>
      )}

      {!carregando && conferido && ordenados.length === 0 && (
        <p className="text-ok-600 text-sm font-medium">
          Nada a corrigir. O contrato pode ser gerado.
        </p>
      )}

      {!carregando && impeditivos > 0 && (
        <p className="text-erro-600 text-sm font-medium">
          {impeditivos} {impeditivos === 1 ? 'impede' : 'impedem'} a geração.
        </p>
      )}

      {ordenados.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {ordenados.map((a, i) => (
            <li
              key={`${a.regra}-${i}`}
              className={`border-l-2 pl-3 ${BORDA[a.severidade]}`}
            >
              <span title={EXPLICACAO[a.severidade]}>
                <Etiqueta tom={TOM[a.severidade]}>{ROTULO[a.severidade]}</Etiqueta>
              </span>
              <p className="text-tinta-900 mt-1 text-sm">{a.mensagem}</p>
              {a.fundamento && (
                <p className="text-tinta-500 mt-0.5 text-xs">{a.fundamento}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
